import { T } from "../../../i18n/social";
import React, { useState, useEffect, useCallback } from "react";
import { fetchAvailableSlots } from "utils/graphApi";

// ─── Skeleton Loader ────────────────────────────────────────────────────────
const SlotSkeleton = () => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
    {[1, 2, 3, 4, 5, 6]?.map((i) => (
      <div
        key={i}
        className="skeleton"
        style={{
          height: '48px',
          borderRadius: '12px',
          animationDelay: `${i * 80}ms`,
        }}
      />
    ))}
  </div>
);

// ─── Empty State ─────────────────────────────────────────────────────────────
const EmptyState = ({ onBack }) => (
  <div style={{
    textAlign: 'center',
    padding: '32px 16px',
    background: '#f8f9fa',
    borderRadius: '16px',
    border: '1px dashed #dde1e7',
  }}>
    <div style={{
      width: '48px', height: '48px',
      borderRadius: '50%',
      background: '#eef0f3',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 12px',
    }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7a8d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    </div>
    <p style={{ fontSize: '14px', fontWeight: 700, color: '#1e2b38', fontFamily: 'var(--font-ui)', marginBottom: '6px' }}>
      Keine freien Termine
    </p>
    <p style={{ fontSize: '13px', color: '#6b7a8d', fontFamily: 'var(--font-ui)', marginBottom: '16px', lineHeight: 1.5 }}>
      An diesem Tag sind leider keine Termine verfügbar. Bitte wähle einen anderen Tag.
    </p>
    <button
      type="button"
      onClick={onBack}
      style={{
        padding: '10px 20px',
        borderRadius: '10px',
        border: '1.5px solid #3E4954',
        background: 'transparent',
        color: '#3E4954',
        fontFamily: 'var(--font-ui)',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      ← Anderen Tag wählen
    </button>
  </div>
);

// ─── Error State ─────────────────────────────────────────────────────────────
const ErrorState = ({ onRetry }) => (
  <div style={{
    textAlign: 'center',
    padding: '24px 16px',
    background: '#fff5f5',
    borderRadius: '16px',
    border: '1px solid #fecaca',
  }}>
    <p style={{ fontSize: '13px', color: '#dc2626', fontFamily: 'var(--font-ui)', marginBottom: '12px' }}>
      Termine konnten nicht geladen werden.
    </p>
    <button
      type="button"
      onClick={onRetry}
      style={{
        padding: '8px 16px',
        borderRadius: '8px',
        border: '1.5px solid #dc2626',
        background: 'transparent',
        color: '#dc2626',
        fontFamily: 'var(--font-ui)',
        fontSize: '12px',
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      Erneut versuchen
    </button>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const StepTimeSlot = ({ selectedDate, selectedSlot, onSelect, consultationType, onBack, source, lang, stepBadge }) => {
  const ts = (lang && T[lang]?.steps?.time) || T['de']?.steps?.time || { title: 'Uhrzeit wählen', selectedDay: 'Gewählter Tag', morning: '☀️ Vormittag', afternoon: '🌤️ Nachmittag', uhr: 'Uhr', back: 'Zurück' };
  const [slots, setSlots] = useState({ morning: [], afternoon: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadSlots = useCallback(async () => {
    if (!selectedDate) return;
    setLoading(true);
    setError(false);
    try {
      const result = await fetchAvailableSlots(selectedDate, consultationType || 'onsite', source);
      setSlots(result);
    } catch (err) {
      console.error('Slot load error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, consultationType]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  const dateLabel = selectedDate
    ? selectedDate?.toLocaleDateString(lang === "uk" ? "uk-UA" : lang === "ru" ? "ru-RU" : "de-DE", {
        weekday: "long",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";

  const hasSlots = slots?.morning?.length > 0 || slots?.afternoon?.length > 0;

  const renderSlot = (slot) => {
    if (!slot?.time) return null;
    const isSel = selectedSlot === slot?.time;
    return (
      <button
        key={slot?.time}
        type="button"
        onClick={() => onSelect(slot?.time, slot?.beraterEmail)}
        className={`time-slot-pill${isSel ? ' selected' : ''}`}
      >
        <span style={{ display: 'block', fontSize: '14px', fontWeight: 700 }}>{slot?.time}{ts.uhr ? ` ${ts.uhr}` : ""}</span>
      </button>
    );
  };

  return (
    <div className="booking-card step-enter">
      {stepBadge && <div className="step-badge">{stepBadge}</div>}

      {/* Selected date banner */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', padding: '14px 16px', background: '#f8f9fa', borderRadius: '14px', border: '1px solid #dde1e7' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#3E4954', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </div>
        <div>
          <p style={{ fontSize: '11px', color: '#6b7a8d', fontFamily: 'var(--font-ui)', fontWeight: 500, marginBottom: '2px' }}>{ts.selectedDay}</p>
          <p style={{ fontSize: '14px', fontWeight: 700, color: '#1e2b38', fontFamily: 'var(--font-ui)', textTransform: 'capitalize' }}>{dateLabel}</p>
        </div>
      </div>

      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '26px', fontWeight: 700, color: '#1e2b38', marginBottom: '20px', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
        {ts.title}
      </h2>

      {/* Loading state */}
      {loading && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <div className="skeleton" style={{ width: '80px', height: '16px', borderRadius: '8px' }} />
          </div>
          <SlotSkeleton />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '20px 0 12px' }}>
            <div className="skeleton" style={{ width: '80px', height: '16px', borderRadius: '8px' }} />
          </div>
          <SlotSkeleton />
        </div>
      )}

      {/* Error state */}
      {!loading && error && <ErrorState onRetry={loadSlots} />}

      {/* Empty state */}
      {!loading && !error && !hasSlots && <EmptyState onBack={onBack} />}

      {/* Slots */}
      {!loading && !error && hasSlots && (
        <>
          {slots?.morning?.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#6b7a8d', fontFamily: 'var(--font-ui)', letterSpacing: '0.02em' }}>{ts.morning}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {slots?.morning?.map(renderSlot)}
              </div>
            </div>
          )}

          {slots?.afternoon?.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#6b7a8d', fontFamily: 'var(--font-ui)', letterSpacing: '0.02em' }}>{ts.afternoon}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {slots?.afternoon?.map(renderSlot)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StepTimeSlot;